import { html, nothing, type TemplateResult } from "lit";

import { msg, str } from "@lit/localize";

import { bezirkName } from "../../config/bezirke.js";
import { STADT_NAME } from "../../config/app-config.js";

/**
 * Kleinteile, die in mehreren Blöcken vorkommen. Bewusst Funktionen statt
 * eigener Elemente: Sie tragen keinen Zustand, und die Styles kommen aus
 * `block-styles.ts` im jeweiligen Shadow Root.
 */

/**
 * Die Auswahl-Zeile — wörtlich aus den Daten, NIE umformuliert.
 * Sie sagt bei jedem Block, wonach ausgewählt wurde; kein Block ohne sie.
 */
export function auswahlZeile(text: string): TemplateResult {
  return html`<p class="auswahl">Auswahl — ${text}</p>`;
}

/**
 * Bezirks-Marker. Wird NUR gezeigt, wenn `bezirk` gesetzt ist.
 *
 * `bezirk: null` darf nicht pauschal als „Stadtweit" beschriftet werden — es
 * gibt Drucksachen mit `ebene: "bv"` UND `bezirk: null`; dort wäre das falsch.
 * Eine Ableitung aus `ebene` ist erst zulässig, wenn sie fachlich geprüft ist.
 */
export function bezirkMarker(bezirk: string | null | undefined): TemplateResult | typeof nothing {
  if (!bezirk) return nothing;
  // Unbekannte Slugs werden sichtbar durchgereicht statt still geschluckt.
  return html`<span class="bezirk-marker">${bezirkName(bezirk) ?? bezirk}</span>`;
}

// -----------------------------------------------------------------------------
// Der Merker: zwei Absichten, ein Besitzer
// -----------------------------------------------------------------------------

/**
 * Die Blöcke MELDEN nur, was jemand wollte — gespeichert wird der Merker an
 * einer einzigen Stelle (der Seite). Zwei Elemente, die beide schreiben,
 * laufen früher oder später auseinander; eines schreibt, alle bekommen den
 * neuen Wert als Property zurück.
 */
export const GEBIET_MERKEN = "gebiet-merken";
export const GEBIET_LOESCHEN = "gebiet-loeschen";

/**
 * Der Rückweg aus der gefilterten Sicht — am FUSS des Blocks.
 *
 * Ohne ihn wäre der Merker eine Einbahnstraße: Man käme in ein Veedel hinein,
 * aber nur über den Einstiegs-Block wieder heraus — und der steht ganz oben.
 * Deshalb trägt ihn jeder gefilterte Block.
 *
 * ER STEHT UNTEN, nicht neben der Auswahl-Zeile: Oben stand er zwischen einer
 * Aussage über die Daten und dem Inhalt, auf den sie sich bezieht — dort las er
 * sich wie ein Teil der Aussage. Unten steht er dort, wo die anderen
 * weiterführenden Wege stehen („Alle Drucksachen →").
 *
 * „Ganz Köln ZEIGEN", nicht „Ganz Köln": Ein Ortsname allein sieht aus wie
 * Navigation; das Verb macht die Handlung kenntlich. Und er benennt das
 * ERGEBNIS statt der Mechanik — „Filterung aufheben" müsste erst übersetzt
 * werden, und „Filter" heißt in dieser Oberfläche sonst nirgends so.
 */
export function stadtweitZurueck(gefiltert: boolean): TemplateResult | typeof nothing {
  if (!gefiltert) return nothing;
  return html`<button
    type="button"
    class="stadtweit"
    @click=${(e: Event) =>
      e.currentTarget?.dispatchEvent(
        new CustomEvent(GEBIET_LOESCHEN, { bubbles: true, composed: true }),
      )}
  >
    ${msg(str`Ganz ${STADT_NAME} zeigen`, { id: "app.start.stadtweit.zeigen" })}
  </button>`;
}
